"use client";

import { usePathname } from "next/navigation";
import { useEffect, useRef } from "react";
import { trackPageView } from "@utils/ga4Tracking";

/**
 * SPA Page View Tracker for Next.js
 * Tracks page views on client-side route changes.
 *
 * Two things this must NOT do, both of which it used to do:
 *
 *  1. Gate on `window.gtag` alone. The root layout defines the gtag stub in a
 *     `beforeInteractive` script on every page load, so that check is always
 *     true — including for visitors who never consented. The resulting
 *     `page_view` sat in `dataLayer` and was replayed into GA the moment a
 *     later consent grant ran `config`. `trackPageView` gates on the stored
 *     consent instead.
 *
 *  2. Fire on the initial mount. `gtag('config', …)` is called with
 *     `send_page_view: true`, which already sends the landing page view;
 *     firing here too double-counted it.
 */
export default function SPAPageViewTracker() {
  const pathname = usePathname();
  const isInitialRender = useRef(true);

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }

    trackPageView(pathname ?? "unknown");
  }, [pathname]);

  return null; // This component doesn't render anything
}
